import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { 
    Box, 
    Typography, 
    CircularProgress, 
    Paper, 
    Stack, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogContentText,
    DialogActions, 
    Button 
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import default_theme from "assets/themes/themes";

import { fetchDraftsRequest, deleteDraftRequest } from "../../redux/drafts/draftsSlice";
import EditDraftDialog from "./EditDraftDialog";
import DraftSolutionCard from "./DraftSolutionCard";
import DraftSolutionDetails from "../DraftSolutionDetails/DraftSolutionDetails";
import { fetchLapRequest, updateLapRequest } from "../../redux/laps/lapSlice";


const DraftSolutions = () => {
    const dispatch = useDispatch();
    const { projectId } = useParams();
    
    // Drafts State
    const { items, loading, error } = useSelector((state) => state.drafts);
    const { data: lap } = useSelector((state) => state.laps);
    const attachedIds = lap?.solution_ids || [];
    const publishedItems = useSelector((state) => state.publishedSolutions?.items || []);

    const isLapPublished = attachedIds.length > 0 && attachedIds.every(
        (id) => publishedItems.some((pubItem) => String(pubItem.id) === String(id) || String(pubItem.entryId) === String(id))
    );

    const [selectedDraft, setSelectedDraft] = useState(null);
    const [editOpen, setEditOpen] = useState(false);
    const [editingDraft, setEditingDraft] = useState(null);
    const [validationErrorOpen, setValidationErrorOpen] = useState(false);
    const [cardDeleteDialogOpen, setCardDeleteDialogOpen] = useState(false);
    const [draftToDelete, setDraftToDelete] = useState(null);

    const confirmDeleteFromCard = (draft) => {
        setDraftToDelete(draft);
        setCardDeleteDialogOpen(true);
    };

    const executeCardDelete = () => {
        if (draftToDelete) {
            handleDelete(draftToDelete);
        }
        setCardDeleteDialogOpen(false);
        setDraftToDelete(null);
    };
    // ==========================================
    // TRACK INLINE BUTTON LOADERS HERE
    // ==========================================
    const [deletingId, setDeletingId] = useState(null);
    const [publishingId, setPublishingId] = useState(null);

    useEffect(() => {
        setDeletingId(null);
        setPublishingId(null);

        if (selectedDraft) {
            const currentId = selectedDraft.id || selectedDraft.entryId;
            const stillInDrafts = items.some((item) => (item.id || item.entryId) === currentId);

            if (!stillInDrafts) {
                setSelectedDraft(null); 
            }
        }
    }, [items, selectedDraft]);
    // ==========================================
    const handleTogglePhase33 = (draft) => {
        if (!lap?.id) return;

        const draftId = Number(draft.id || draft.entryId);
        const isSelected = attachedIds.includes(draftId);

        // --- NEW: Validation Check before adding ---
        if (!isSelected) {
            const data = draft.combinedData || {};
            const title = data.title_en || data.title_english || data.title;
            const story = data.story || data.aims || data.aim;
            const country = data.location_country;
            const rdd = data.rdd_name || data.rdd;

            if (!title || !title.trim() || !story || !story.trim() || !country || !country.trim() || !rdd) {
                setValidationErrorOpen(true);
                return;
            }
        }

        // Prevent adding more than 3
        if (!isSelected && attachedIds.length >= 3) {
            return;
        }

        const nextIds = isSelected
            ? attachedIds.filter((id) => id !== draftId)
            : [...attachedIds, draftId];

        dispatch(updateLapRequest({
            lapId: lap.id,
            projectId,
            data: {
                title: lap.title || "",
                description: lap.description || "",
                solution_ids: nextIds,
            },
        }));
    };
    
    const handleClose = () => {
        setSelectedDraft(null);
    };

    const handleEditDraft = (draft) => {
        setSelectedDraft(null);
        setEditingDraft(draft);
        setEditOpen(true);
    };

    const handleCloseEdit = () => {
        setEditOpen(false);
        setEditingDraft(null);
    };


    const handleDelete = (draft) => {
        const targetId = draft.id || draft.entryId;
        setDeletingId(targetId);
        dispatch(deleteDraftRequest({ draftId: targetId, projectId }));
    };
    
    useEffect(() => {
        if (projectId) {
            dispatch(fetchDraftsRequest(projectId));
        }
    }, [dispatch, projectId]);

    if (error) {
        return (
            <Box sx={{ p: 2.5, borderRadius: 3, backgroundColor: default_theme.palette.other.lightOrange, color: default_theme.palette.other.darkBlue }}>
                <Typography sx={{ fontWeight: 700, mb: 0.5, color: default_theme.palette.other.addButtonBlue }}>
                    Could not load solutions
                </Typography>
                <Typography color="error" sx={{ fontSize: "0.9rem" }}>{error}</Typography>
            </Box>
        );
    }

    return (
        <>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                
                {/* ========================================== */}
                {/* SECTION 1: DRAFTED SOLUTIONS               */}
                {/* ========================================== */}
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: default_theme.palette.other.addButtonBlue, mb: 1 }}>
                        Drafted Solutions ({items.length || 0})
                    </Typography>

                    {/* ONLY show big loader if loading AND the list is empty */}
                    {loading && items.length === 0 ? (
                        <Paper elevation={0} sx={{ p: 4, textAlign: "center", borderRadius: 3, border: `1px dashed ${default_theme.palette.border.subtleLight}` }}>
                            <CircularProgress size={30} sx={{ color: default_theme.palette.other.chatGreen }} />
                            <Typography variant="body2" sx={{ mt: 1, color: default_theme.palette.other.textSecondary }}>
                                Loading Drafts ...
                            </Typography>
                        </Paper>
                    ) : items.length === 0 ? (
                        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: `1px dashed ${default_theme.palette.border.subtleLight}`, backgroundColor: default_theme.palette.other.backgroundSubtle }}>
                            <Typography variant="body2" sx={{ color: default_theme.palette.other.textSecondary }}>
                                No saved drafts yet. Create a solution above and save it to drafts.
                            </Typography>
                        </Paper>
                    ) : (
                        <Box sx={{
                            maxHeight: "580px", 
                            overflowY: "auto",
                            overflowX: "hidden",
                            p: 2,
                            pr: 1, 
                            backgroundColor: "#F4F7F9",
                            borderRadius: "8px", 
                            border: "1px solid #E4E7EC", 
                            boxShadow: "inset 0 4px 6px rgba(0,0,0,0.04)"
                        }}>
                            <Stack spacing={2}>
                                {items.map((draft) => {
                                    const draftId = Number(draft.id || draft.entryId);
                                    const isSelectedForPhase3_3 = attachedIds.includes(draftId);
                                    const isSelectingPhase3_3 = !isSelectedForPhase3_3 && attachedIds.length >= 3;

                                    return (
                                        <DraftSolutionCard
                                            key={draft.entryId}
                                            draft={draft}
                                            onView={() => setSelectedDraft({ ...draft, isPublished: false })}
                                            onAddToPhase3_3={isLapPublished ? null : () => handleTogglePhase33(draft)}
                                            onEdit={() => handleEditDraft(draft)}
                                            onDelete={() => confirmDeleteFromCard({ ...draft, isPublished: false })}
                                            isSelectedForPhase3_3={isSelectedForPhase3_3}
                                            isSelectingPhase3_3={isSelectingPhase3_3}
                                            isDeleting={deletingId === (draft.id || draft.entryId)}
                                        />
                                    );
                                })}
                            </Stack>
                        </Box>
                    )}
                </Box>
            </Box>

            <DraftSolutionDetails
                open={Boolean(selectedDraft)}
                draft={selectedDraft}
                onClose={handleClose}
                onEdit={handleEditDraft}
                onDelete={handleDelete}
                isPublished={selectedDraft?.isPublished}
                // FIXED: Passing inline delete state to the modal!
                isDeleting={deletingId === (selectedDraft?.id || selectedDraft?.entryId)}
            />

            <EditDraftDialog
                open={editOpen}
                draft={editingDraft}
                onClose={handleCloseEdit}
            />
            
            <Dialog 
                open={validationErrorOpen} 
                onClose={() => setValidationErrorOpen(false)}
                PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, color: default_theme.palette.other.addButtonBlue }}>
                    Missing Required Fields
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Cannot add to Phase 3.3: <strong>English Title</strong>, <strong>Story</strong>, <strong>Country</strong>, and <strong>RDD</strong> are mandatory fields.
                    </Typography>
                    <Typography sx={{ mt: 1 }}>
                        Please click on the Edit icon or on "View Details" then "Edit Draft" to fill them out before adding to the LAP.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setValidationErrorOpen(false)}
                        variant="contained"
                        sx={{
                            backgroundColor: default_theme.palette.other.addButtonBlue,
                            color: "#fff",
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: 2,
                        }}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog 
                open={cardDeleteDialogOpen} 
                onClose={() => setCardDeleteDialogOpen(false)}
            >
                <DialogTitle sx={{ fontWeight: "bold", color: "#d32f2f" }}>
                    {draftToDelete?.isPublished ? "Delete Published Solution?" : "Delete Draft?"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this {draftToDelete?.isPublished ? "solution" : "draft"}? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setCardDeleteDialogOpen(false)} sx={{ color: "#667085", fontWeight: 600 }}>
                        Cancel
                    </Button>
                    <Button onClick={executeCardDelete} variant="contained" color="error" sx={{ fontWeight: 600 }}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default DraftSolutions;